import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import Name from './Name'
import SetNumber from '../../set/SetNumber'
import { translationsMapping } from '../../../../config'
import Rarity from '../Rarity'
import Tooltip from '../../general/Tooltip'
import rarities from '../../../data/rarities'
import Artwork, { StyledArtwork } from '../Artwork'
import { desktop, mobile } from '../../../styles/media'
import { TCG } from '../../../data/formats'
import { StyledReleaseDate } from '../../text/ReleaseDate'
import { StyledAlternateSetNumbers } from '../AlternateSetNumbers'
import RegionOnlyFlag, { StyledRegionOnlyFlag } from '../../text/RegionOnlyFlag'
import { StyledFlag } from '../../general/Flag'
import LanguageAbbr from './LanguageAbbr'
import Decoration from './Decoration'

const SetName = ({ artworkNumber, artworkCategory, cardNumber, children, decoration, format, hasRarityTag, highlight, language, name, rarityId, releaseEu, releaseNa, region, setNumber, showCardNumber, showRegionOnlyFlag, small }) => (
	<Name flexColumn={!small}>
		{hasRarityTag && (
			<Tooltip title={(rarities[rarityId] && rarities[rarityId].name) || ''}>
				<Rarity id={rarityId}>{(rarities[rarityId] && rarities[rarityId].abbr) || '?'}</Rarity>
			</Tooltip>
		)}

		{!hasRarityTag && (
			<StyledSetNumber
				format={format}
				small={small}
			>
				{setNumber || '?'}
			</StyledSetNumber>
		)}

		<NameInner small={small}>
			<SetNameInnerTop>
				{highlight && highlight !== '' && (
					<Highlighter
						highlightClassName="highlightedText"
						searchWords={highlight.split(' ')}
						autoEscape
						textToHighlight={name}
					/>
				)}
				{(!highlight || highlight === '') && name}

				{showCardNumber && cardNumber && `${setNumber || '?'}-${language}${cardNumber}`}

				{region && (
					<LanguageAbbr language={region.language} />
				)}

				{artworkNumber !== null && artworkNumber !== 1 && <Artwork number={artworkNumber} category={artworkCategory} />}

				{showRegionOnlyFlag && <RegionOnlyFlag releaseEu={releaseEu} releaseNa={releaseNa} />}

				{decoration && <Decoration decoration={decoration} />}
			</SetNameInnerTop>
			{children && (
				<NameInnerBottom>
					{children}
				</NameInnerBottom>
			)}
		</NameInner>
	</Name>
)

SetName.propTypes = {
	artworkNumber: PropTypes.number,
	artworkCategory: PropTypes.string,
	cardNumber: PropTypes.string,
	children: PropTypes.any,
	decoration: PropTypes.object,
	format: PropTypes.number,
	highlight: PropTypes.string,
	language: PropTypes.string.isRequired,
	name: PropTypes.string,
	rarityId: PropTypes.number,
	releaseEu: PropTypes.any,
	releaseNa: PropTypes.any,
	region: PropTypes.object,
	hasRarityTag: PropTypes.bool,
	setNumber: PropTypes.string,
	showCardNumber: PropTypes.bool,
	showRegionOnlyFlag: PropTypes.bool,
	small: PropTypes.bool
}

SetName.defaultProps = {
	artworkNumber: null,
	artworkCategory: null,
	cardNumber: null,
	children: null,
	decoration: null,
	format: TCG,
	hasRarityTag: false,
	highlight: '',
	name: null,
	rarityId: null,
	releaseEu: null,
	releaseNa: null,
	region: null,
	setNumber: null,
	showCardNumber: false,
	showRegionOnlyFlag: false,
	small: true
}

const NameInner = styled.div`
	display: flex;
	flex-direction: column;

	${props => !props.small && `
		margin-left: 0;
	`}
`

export const SetNameInnerTop = styled.div`
	${Name} {
		margin-left: 0.5rem;
	}

	${StyledArtwork} {
		margin-left: 0.5rem;
	}

	${desktop`
		${StyledRegionOnlyFlag} {
			position: relative;
			top: 2px;
		}
	`}
`

const NameInnerBottom = styled.div`
	margin-top: 0.15rem;

	${StyledReleaseDate} {
		font-size: 0.9rem;

		${StyledRegionOnlyFlag} {
			height: 14px;
			margin-left: 0.35rem;
			order: 1;
		}

		${StyledFlag} {
			height: 14px;
			width: 14px;
		}
	}

	${StyledAlternateSetNumbers} {
		color: ${props => props.theme.textLight};
	}

	${desktop`
		display: none;
	`}

	${mobile`
		${StyledReleaseDate} {
			font-size: 0.8rem;
		}

		${StyledFlag} {
			border-radius: 1px !important;
			height: 12px !important;
			width: 12px !important;
		}
	`}
`

export const SetCoverCard = styled.div`
	border-radius: 2px;
	box-shadow: 0 0 3px ${props => props.theme.shadowNormal};
	height: 25px;
	width: 50px;
	min-width: 50px;
	overflow: hidden;

	align-items: center;
	display: flex;
	justify-content: center;

	img {
		height: 50px;
		width: 50px;
	}

	${props => props.large && `
		border-radius: 4px;
		height: 75px;
		width: 150px;
		min-width: 150px;

		img {
			height: 150px;
			width: 150px;
		}
	`}

	${props => props.position === 'top' && `
		align-items: flex-start;
	`}

	${props => props.position === 'bottom' && `
		align-items: flex-end;
	`}

	${mobile`
		${props => props.large && `
			margin-right: 0;
			margin-bottom: 1rem;
		`}
	`}
`

export const StyledSetNumber = styled(SetNumber)`
	${desktop`
		${props => !props.small && `
			margin-right: 1rem;
		`}
	`}

	${mobile`
		${props => !props.small && `
			margin-bottom: 1rem;
		`}
	`}
`

const mapStateToProps = state => ({
	language: translationsMapping[state.persist.language].toUpperCase()
})

const enhance = connect(mapStateToProps)

export default enhance(SetName)
